import type { OnInit } from '@angular/core';
import {
  Component, EventEmitter, Input, Output, ViewChild, computed, inject,
} from '@angular/core';

import { AvatarComponent, TypeaheadComponent } from '@echospaces-web-monorepo/es-shared-ui';

import { CompanyMembersService, type OrgMember } from './company-members.service';

/**
 * Thin domain-specific wrapper around the generic `TypeaheadComponent`
 * that resolves a user (or several) from the current organization's
 * member directory. The generic carries the picker mechanics — single /
 * multi mode, chips, dropdown, outside-click — this wrapper supplies the
 * data source (`CompanyMembersService`) and a 2-line + avatar suggestion
 * row.
 *
 * Public API is preserved from the earlier hand-rolled picker, plus
 * `[multiple]` / `[values]` for bulk selection.
 */
@Component({
  selector: 'es-org-user-picker',
  imports: [AvatarComponent, TypeaheadComponent],
  template: `
    <es-typeahead
      [items]="members.members()"
      [loading]="members.loading()"
      [multiple]="multiple"
      [placeholder]="placeholder"
      [value]="resolvedValue()"
      [values]="resolvedValues()"
      [trackByFn]="trackBy"
      [labelFn]="label"
      [sublabelFn]="sublabel"
      [itemTemplate]="memberRow"
      noItemsText="No members in this organization"
      allPickedText="All members are picked"
      (itemSelected)="userSelected.emit($event)"
      (selectionChanged)="selectionChanged.emit($event)"
    ></es-typeahead>

    <ng-template #memberRow let-member>
      <div class="flex items-center gap-3 px-3 py-2">
        <es-avatar [name]="member.fullName ?? member.email" size="sm"></es-avatar>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{{ member.fullName ?? member.email }}</p>
          @if (member.fullName) {
            <p class="text-xs text-[var(--text-secondary)] truncate">{{ member.email }}</p>
          }
        </div>
      </div>
    </ng-template>
  `,
})
export class OrgUserPickerComponent implements OnInit {
  @Input() placeholder = 'Search members by name or email…';
  @Input() value: string | null = null;
  @Input() values: string[] = [];
  @Input() multiple = false;

  @Output() userSelected = new EventEmitter<OrgMember>();
  @Output() selectionChanged = new EventEmitter<OrgMember[]>();

  @ViewChild(TypeaheadComponent) private typeahead?: TypeaheadComponent<OrgMember>;

  readonly members = inject(CompanyMembersService);

  readonly trackBy = (m: OrgMember) => m.userId;
  readonly label = (m: OrgMember) => m.fullName ?? m.email;
  readonly sublabel = (m: OrgMember) => (m.fullName ? m.email : undefined);

  readonly resolvedValue = computed<OrgMember | null>(() => {
    if (this.multiple || !this.value) return null;
    return this.members.findById(this.value) ?? null;
  });

  readonly resolvedValues = computed<OrgMember[]>(() => {
    if (!this.multiple) return [];
    return this.values
      .map((id) => this.members.findById(id))
      .filter((m): m is OrgMember => !!m);
  });

  async ngOnInit(): Promise<void> {
    await this.members.ensureLoaded();
  }

  /** Imperatively clear the multi-mode selection. Delegates to the underlying typeahead. */
  clearSelection(): void {
    this.typeahead?.clearSelection();
  }
}
